import { type ReactNode, useCallback, useMemo } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveLinear, curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import classNames from 'classnames';
import { tooltipContainerClassName } from './BarChart';
import { ChartAverageMarker, type ChartMarker } from './ChartAverageMarker';
import useScreenSize from '../../hooks/useScreenSize';
import { getDateAxisTicks } from '../../utils/charts';
import { formatWithNumeral } from '../../utils/numbers';
import LoadingSpinner from '../LoadingSpinner';

export type LineChartDatum = {
  x: Date | number;
  y: number;
};

export type LineChartSeries = {
  id: string;
  data: LineChartDatum[];
  color: string;
  /** When true the line is not rendered but is still used for hover detection */
  hidden?: boolean;
};

export type LineChartMarker = ChartMarker;

export type LineChartVerticalLine = {
  /** x position in data space */
  x: number;
  /** bottom of the line in data space */
  y1: number;
  /** top of the line in data space */
  y2: number;
  label: string;
};

export type LineChartProps = {
  /** Single-series mode */
  data?: LineChartDatum[];
  color?: string;
  /** Multi-series mode — overrides data/color when provided */
  series?: LineChartSeries[];
  /** 'time' (default) uses a date x-axis; 'linear' uses a numeric x-axis */
  xType?: 'time' | 'linear';
  /** 'monotoneX' (default) or 'linear' */
  curve?: 'monotoneX' | 'linear';
  margin?: { top: number; right: number; bottom: number; left: number };
  xTickFormat?: (value: Date | number) => string;
  xTickValues?: (Date | number)[];
  yTickValues?: number[];
  yTickFormat?: (value: number) => string;
  /** Fixed y-axis domain [min, max]. Defaults to [0, max]; pass a negative min for charts that cross zero */
  yDomain?: [number, number];
  /** Hide the y-axis zero tick label (default true) */
  hideZero?: boolean;
  /** Draw a solid baseline at y=0 — for charts that cross zero, e.g. deltas */
  solidBaseLine?: boolean;
  /** Show a hover dot for a hidden first (reference) series. Default true */
  showHiddenReferenceDot?: boolean;
  /** Render a dot at every datum — for series with too few points to draw a visible line */
  showDots?: boolean;
  /** Receives the hovered datum. y is the value of the first (reference) series at that x */
  renderTooltip: (datum: LineChartDatum) => ReactNode;
  tooltipClassName?: string;
  /** Optional horizontal reference lines with pill labels */
  markers?: LineChartMarker[];
  /** Optional vertical annotation lines */
  verticalLines?: LineChartVerticalLine[];
  gridStroke?: string;
  axisTickColor?: string;
  strokeWidth?: number;
};

const DEFAULT_MARGIN = { top: 20, right: 20, bottom: 50, left: 50 };

/** Convert a Date or number x-value to a comparable number */
const toXNum = (x: Date | number): number => (typeof x === 'number' ? x : x.getTime());

const LineChartInner = ({
  width,
  height,
  data,
  color = '#46DA93',
  series: seriesProp,
  xType = 'time',
  curve: curveProp = 'monotoneX',
  margin = DEFAULT_MARGIN,
  xTickFormat = (value: Date | number) => {
    if (value instanceof Date) {
      return new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    return String(value);
  },
  xTickValues: xTickValuesProp,
  yTickValues: yTickValuesProp,
  yTickFormat = (value) => String(formatWithNumeral(value, false, true)),
  yDomain,
  hideZero = true,
  solidBaseLine = false,
  showHiddenReferenceDot = true,
  showDots = false,
  renderTooltip,
  tooltipClassName = tooltipContainerClassName,
  markers,
  verticalLines,
  gridStroke = '#424242',
  axisTickColor = '#909090',
  strokeWidth = 1.5,
}: LineChartProps & { width: number; height: number }) => {
  const { isMobile } = useScreenSize();

  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } =
    useTooltip<LineChartDatum>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const allSeries: LineChartSeries[] = useMemo(
    () => seriesProp ?? [{ id: 'default', data: data ?? [], color }],
    [seriesProp, data, color],
  );

  const curveFactory = curveProp === 'linear' ? curveLinear : curveMonotoneX;

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const xScale = useMemo(() => {
    const xs = allSeries.flatMap((s) => s.data.map((d) => toXNum(d.x)));
    if (xType === 'linear') {
      return scaleLinear<number>({
        domain: xs.length ? [Math.min(...xs), Math.max(...xs)] : [0, 1],
        range: [0, xMax],
      });
    }
    return scaleTime<number>({
      domain: xs.length ? [Math.min(...xs), Math.max(...xs)] : [0, 1],
      range: [0, xMax],
    });
  }, [allSeries, xMax, xType]);

  const yScale = useMemo(() => {
    const maxY = allSeries.length
      ? Math.max(...allSeries.flatMap((s) => s.data.map((d) => d.y)), 0)
      : 0;
    return scaleLinear<number>({
      domain: yDomain ?? [0, maxY * 1.2 || 1],
      range: [yMax, 0],
      nice: true,
    });
  }, [allSeries, yMax, yDomain]);

  const referenceData = allSeries[0]?.data ?? [];

  const xTickValues = useMemo(() => {
    if (xTickValuesProp) return xTickValuesProp;
    if (xType === 'linear') return undefined; // let visx auto-compute
    return getDateAxisTicks(referenceData.map((d) => d.x as Date));
  }, [xTickValuesProp, xType, referenceData]);

  const yTickCount = useMemo(() => {
    const tickLength = yScale.ticks(5).length;
    return tickLength > 5 ? 5 : tickLength;
  }, [yScale]);

  const hoveredIdx = useMemo(() => {
    if (!tooltipData) return null;
    const hoveredX = toXNum(tooltipData.x);
    const idx = referenceData.findIndex((d) => toXNum(d.x) === hoveredX);
    return idx >= 0 ? idx : null;
  }, [tooltipData, referenceData]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const coords = localPoint(event);
      if (!coords || !referenceData.length) return;
      const raw = xScale.invert(coords.x - margin.left);
      const xMs = typeof raw === 'number' ? raw : raw.getTime();
      let closestIdx = 0;
      let minDiff = Infinity;
      for (let i = 0; i < referenceData.length; i += 1) {
        const diff = Math.abs(toXNum(referenceData[i].x) - xMs);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }
      const closest = referenceData[closestIdx];
      showTooltip({
        tooltipLeft: xScale(toXNum(closest.x)) + margin.left,
        tooltipTop: coords.y,
        tooltipData: { x: closest.x, y: allSeries[0]?.data[closestIdx]?.y ?? 0 },
      });
    },
    [referenceData, allSeries, xScale, margin, showTooltip],
  );

  if (width < 10 || height < 10) return null;

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={containerRef} width={width} height={height} role="application">
        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={xMax}
            numTicks={yTickCount}
            stroke={gridStroke}
            strokeWidth={1}
            strokeDasharray="2"
          />
          {solidBaseLine && (
            <line
              x1={0}
              x2={xMax}
              y1={yScale(0)}
              y2={yScale(0)}
              stroke={axisTickColor}
              strokeWidth={1}
            />
          )}
          {markers?.map((marker) => (
            <ChartAverageMarker
              key={marker.label}
              marker={marker}
              y={yScale(marker.value)}
              xMax={xMax}
            />
          ))}
          {verticalLines?.map((vl) => {
            const diffBetweenLines =
              verticalLines.length > 1
                ? Math.abs(
                    (verticalLines.find((l) => l.label === 'Optimal')?.x ?? 0) -
                      (verticalLines.find((l) => l.label === 'Current')?.x ?? 0),
                  )
                : Infinity;
            const maxX = Math.max(...verticalLines.map((l) => l.x));
            const textOffset = diffBetweenLines < 2 && vl.x === maxX ? 16 : 4;
            const x = xScale(vl.x);
            const y1 = yScale(vl.y1);
            const y2 = yScale(vl.y2);
            return (
              <g key={`${vl.label}-${vl.x}`} style={{ pointerEvents: 'none' }}>
                <line
                  x1={x}
                  x2={x}
                  y1={y1}
                  y2={y2}
                  stroke="#FF33AF"
                  strokeWidth={2}
                  strokeDasharray="2"
                />
                <text
                  fontWeight={500}
                  fontFamily="Aeonik-Regular"
                  textAnchor={vl.x >= 3 ? 'middle' : undefined}
                  fontSize={12}
                  x={x}
                  y={y2 - textOffset}
                >
                  <tspan fill="#909090">{vl.label}: </tspan>
                  <tspan fill="#FFFFFF">{vl.x}%</tspan>
                </text>
              </g>
            );
          })}
          {allSeries.map((s) =>
            s.hidden ? null : (
              <LinePath
                key={s.id}
                data={s.data}
                x={(d) => xScale(toXNum(d.x))}
                y={(d) => yScale(d.y)}
                stroke={s.color}
                strokeWidth={strokeWidth}
                curve={curveFactory}
              />
            ),
          )}
          {showDots &&
            allSeries.map((s) =>
              s.hidden
                ? null
                : s.data.map((d) => (
                    <circle
                      key={`${s.id}-${toXNum(d.x)}`}
                      cx={xScale(toXNum(d.x))}
                      cy={yScale(d.y)}
                      r={3}
                      fill={s.color}
                    />
                  )),
            )}
          {tooltipData && (
            <line
              x1={xScale(toXNum(tooltipData.x))}
              x2={xScale(toXNum(tooltipData.x))}
              y1={0}
              y2={yMax}
              stroke="#FFFFFF"
              strokeOpacity={0.4}
              strokeWidth={1}
              strokeDasharray="2"
              style={{ pointerEvents: 'none' }}
            />
          )}
          {tooltipData &&
            allSeries.map((s) => {
              if (s.hidden) return null;
              // Match by x rather than index: a series may skip x values the reference has.
              const point = s.data.find((d) => toXNum(d.x) === toXNum(tooltipData.x));
              if (!point) return null;
              const cx = xScale(toXNum(point.x));
              const cy = yScale(point.y);
              return (
                <g key={s.id} style={{ pointerEvents: 'none' }}>
                  <circle cx={cx} cy={cy} r={6} fill={s.color} fillOpacity={0.2} />
                  <circle cx={cx} cy={cy} r={3} fill={s.color} />
                </g>
              );
            })}
          {/* dots for hidden series shown via reference data */}
          {showHiddenReferenceDot &&
            hoveredIdx !== null &&
            referenceData[hoveredIdx] &&
            allSeries[0]?.hidden && (
              <g style={{ pointerEvents: 'none' }}>
                <circle
                  cx={xScale(toXNum(referenceData[hoveredIdx].x))}
                  cy={yScale(referenceData[hoveredIdx].y)}
                  r={6}
                  fill={allSeries[1]?.color ?? '#46DA93'}
                  fillOpacity={0.2}
                />
                <circle
                  cx={xScale(toXNum(referenceData[hoveredIdx].x))}
                  cy={yScale(referenceData[hoveredIdx].y)}
                  r={3}
                  fill={allSeries[1]?.color ?? '#46DA93'}
                />
              </g>
            )}
          {/* Hover detection overlay — rendered last so it captures all mouse events */}
          <rect
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={hideTooltip}
          />
          <AxisBottom
            top={yMax}
            scale={xScale}
            tickValues={xTickValues as Date[] | undefined}
            tickFormat={(d) => xTickFormat(d as Date | number)}
            stroke="transparent"
            tickStroke="transparent"
            tickLength={0}
            tickLabelProps={{
              fill: axisTickColor,
              fontFamily: 'Aeonik-Medium',
              fontSize: 12,
              textAnchor: 'end',
              angle: isMobile ? -60 : 0,
              dy: isMobile ? '-0.10em' : '0.50em',
              dx: isMobile ? '0.33em' : '1.2em',
            }}
          />
          <AxisLeft
            scale={yScale}
            numTicks={yTickValuesProp ? undefined : yTickCount}
            tickValues={yTickValuesProp}
            hideZero={hideZero}
            tickFormat={(value) => yTickFormat(value as number)}
            stroke="transparent"
            tickStroke="transparent"
            tickLength={0}
            tickLabelProps={{
              fill: axisTickColor,
              fontFamily: 'Aeonik-Medium',
              fontSize: 12,
              textAnchor: 'end',
              dx: '-0.33em',
              dy: '0.33em',
            }}
          />
        </Group>
      </svg>
      {tooltipOpen &&
        tooltipData &&
        (isMobile ? (
          <div className="pointer-events-none fixed inset-0 z-10 flex items-center justify-center">
            <div className={tooltipClassName}>{renderTooltip(tooltipData)}</div>
          </div>
        ) : (
          <TooltipInPortal
            top={tooltipTop}
            left={tooltipLeft}
            unstyled
            applyPositionStyle
            className="pointer-events-none z-10"
          >
            <div className={tooltipClassName}>{renderTooltip(tooltipData)}</div>
          </TooltipInPortal>
        ))}
    </div>
  );
};

export const LineChart = ({
  isLoading,
  emptyMessage = 'No data',
  className = 'h-[350px]',
  ...props
}: LineChartProps & { isLoading: boolean; emptyMessage?: string; className?: string }) => {
  const hasData =
    (props.data?.length ?? 0) > 0 || (props.series ?? []).some((s) => s.data.length > 0);

  return (
    <div className={classNames(className, 'relative')}>
      {isLoading || !hasData ? (
        <>
          <img
            src="/bar-chart-placeholder.png"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-50 blur-sm"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <span className="font-aeonikMedium text-cf-light-3">{emptyMessage}</span>
            )}
          </div>
        </>
      ) : (
        <ParentSize>
          {({ width, height }) => <LineChartInner {...props} width={width} height={height} />}
        </ParentSize>
      )}
    </div>
  );
};
